import { HUD } from '../../config/tuning';
import { clamp } from '../../flight/Aerodynamics';
import type { HudPainter } from '../HudLayout';
import type { HudFrame } from '../HudFrame';

/**
 * Missile warning (SPEC §3.4.1): a screen-edge directional caret plus closure-rate ticks.
 *
 * The tick rate rises with closure, which gives the player a time-to-impact estimate without a
 * countdown. A number would be more precise and much worse: it invites arithmetic at the exact
 * moment the player should be flying, whereas a quickening beat is read with the same part of the
 * brain that is already busy.
 */

/** Tick frequency for a closure rate, Hz. Exported to be testable without a canvas. */
export function closureTickHz(closureMs: number): number {
  const normalised = clamp(closureMs / HUD.missileClosureRefMs, 0, 1);
  return HUD.missileTickMinHz + (HUD.missileTickMaxHz - HUD.missileTickMinHz) * normalised;
}

/**
 * Where a relative bearing meets the edge of the display. Returns the point on the rectangle's
 * boundary in the direction of the threat, inset by the margin.
 */
export function edgePointForBearing(
  relativeBearingRad: number,
  widthPx: number,
  heightPx: number,
  marginPx: number,
): { x: number; y: number; angleRad: number } {
  const halfWidth = widthPx * 0.5 - marginPx;
  const halfHeight = heightPx * 0.5 - marginPx;

  // Screen-space direction: bearing 0 is up (off the nose), positive clockwise.
  const dirX = Math.sin(relativeBearingRad);
  const dirY = -Math.cos(relativeBearingRad);

  const scaleX = dirX === 0 ? Number.POSITIVE_INFINITY : halfWidth / Math.abs(dirX);
  const scaleY = dirY === 0 ? Number.POSITIVE_INFINITY : halfHeight / Math.abs(dirY);
  const scale = Math.min(scaleX, scaleY);

  return {
    x: widthPx * 0.5 + dirX * scale,
    y: heightPx * 0.5 + dirY * scale,
    angleRad: Math.atan2(dirY, dirX),
  };
}

export function drawMissileWarning(painter: HudPainter, frame: HudFrame): void {
  if (frame.missileWarnings.length === 0) return;

  const layout = painter.layout;
  const ctx = painter.ctx;
  const margin = layout.px(HUD.marginPx) * 0.8;
  const caret = layout.px(HUD.missileCaretPx);
  const tick = layout.px(HUD.missileTickPx);

  for (let i = 0; i < frame.missileWarnings.length; i += 1) {
    const warning = frame.missileWarnings[i];
    const edge = edgePointForBearing(
      warning.relativeBearingRad,
      layout.widthPx,
      layout.heightPx,
      margin,
    );

    const hz = closureTickHz(warning.closureMs);
    const lit = Math.floor(frame.simTimeS * hz) % 2 === 0;

    ctx.save();
    ctx.translate(edge.x, edge.y);
    ctx.rotate(edge.angleRad);

    // Caret pointing outward, toward the threat.
    ctx.beginPath();
    ctx.moveTo(caret * 0.5, 0);
    ctx.lineTo(-caret * 0.4, -caret * 0.45);
    ctx.lineTo(-caret * 0.4, caret * 0.45);
    ctx.closePath();
    painter.stroke(1.4, HUD.dangerColour);

    if (lit) {
      // Closure ticks stacked behind the caret; the faster they blink, the less time there is.
      for (let t = 1; t <= 3; t += 1) {
        const offset = -caret * 0.4 - tick * t;
        painter.line(offset, -tick * 0.5, offset, tick * 0.5, 1.4, HUD.dangerColour);
      }
    }

    ctx.restore();
  }
}
